import authApiService from "./userApiService"; // Ensure this is your axios instance with interceptors

// Helper function to handle errors
const handleError = (error) => {
  console.error('Tournament Poker Chips API Error:', error);
  return {
    success: false,
    error: error.response?.data?.message || error.response?.data?.error || error.message || 'An unexpected error occurred',
    statusCode: error.response?.status,
    details: error.response?.data?.details || null
  };
};

// Helper function to handle successful responses
const handleSuccess = (response, message = null) => {
  return {
    success: true,
    data: response.data,
    message: message || response.data?.message || 'Operation completed successfully',
    statusCode: response.status
  };
};

export const tournamentChipsService = {
  // Get all records with pagination and filtering
  async getAllRecords(page = 1, limit = 10, filters = {}) {
    try {
      const { status, startDate, endDate, sortBy, sortOrder } = filters;
      const response = await authApiService.get("/tournament-poker-chips", {
        params: {
          page,
          limit,
          status,
          startDate,
          endDate,
          sortBy,
          sortOrder
        }
      });
      return handleSuccess(response, 'Records retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get record by ID
  async getRecordById(id) {
    try {
      const response = await authApiService.get(`/tournament-poker-chips/${id}`);
      return handleSuccess(response, 'Record retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get record by date
  async getRecordByDate(date) {
    try {
      const response = await authApiService.get(`/tournament-poker-chips/date/${date}`);
      return handleSuccess(response, 'Record retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get save logs by date
  async getSaveLogsByDate(date) {
    try {
      const response = await authApiService.get(`/tournament-poker-chips/logs/${date}`);
      return handleSuccess(response, 'Save logs retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Create new record
  async createRecord(recordData) {
    try {
      const response = await authApiService.post("/tournament-poker-chips", recordData);
      return handleSuccess(response, 'Record created successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Update record
  async updateRecord(id, updateData) {
    try {
      const response = await authApiService.put(`/tournament-poker-chips/${id}`, updateData);
      return handleSuccess(response, 'Record updated successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Update starting balance
  async updateStartingBalance(id, startingCash, startingChips) {
    try {
      const response = await authApiService.put(`/tournament-poker-chips/${id}/starting-balance`, {
        startingCash,
        startingChips
      });
      return handleSuccess(response, 'Starting balance updated successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Update closing balance
  async updateClosingBalance(id, closingCash, closingChips) {
    try {
      const response = await authApiService.put(`/tournament-poker-chips/${id}/closing-balance`, {
        closingCash,
        closingChips
      });
      return handleSuccess(response, 'Closing balance updated successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Delete record
  async deleteRecord(id) {
    try {
      const response = await authApiService.delete(`/tournament-poker-chips/${id}`);
      return handleSuccess(response, 'Record deleted successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get summary report
  async getSummaryReport(startDate, endDate) {
    try {
      const response = await authApiService.get("/tournament-poker-chips/summary-report", {
        params: { startDate, endDate }
      });
      return handleSuccess(response, 'Summary report retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get dashboard stats
  async getDashboardStats() {
    try {
      const response = await authApiService.get("/tournament-poker-chips/dashboard");
      return handleSuccess(response, 'Dashboard stats retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Update record status
  async updateStatus(id, status) {
    try {
      const response = await authApiService.put(`/tournament-poker-chips/${id}/status`, { status });
      return handleSuccess(response, 'Status updated successfully');
    } catch (error) {
      return handleError(error);
    }
  }
};

export default tournamentChipsService;
